import React, { useRef } from "react";
import { FaArrowLeft, FaArrowRight, FaEnvelope, FaMapMarkerAlt, FaPhoneAlt, FaStar } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import Logo from "../components/Common/Logo";
import Person from "../assets/Images/person.jpg";
import EeezoHome from "../assets/Images/eeezoHome.png";
import Accordion from "../components/elements/Accordian/Accordian";
import Webenza from "../assets/Images/webenza.jpg";
import eeezoJob from "../assets/Images/eeezoJob.png";
import eeezoTestimonial from "../assets/Images/eeezoTestimonial.png";
import fblogo from "../assets/Images/fblogo.png";
import instalogo from "../assets/Images/instalogo.png";
import linkedinlogo from "../assets/Images/linkedinlogo.png";
import twitterlogo from "../assets/Images/twitterlogo.png";
import youtubelogo from "../assets/Images/youtubelogo.png";
import "./LandingPage.css";
import { clearAuthSession, getAuthToken, getMetadata } from "../utils/encryptedStorage";

const LandingPage = () => {
  const navigate = useNavigate();
  const metadata = getMetadata();
  const token = getAuthToken();
  const jobsRef = useRef(null);
  const testimonialsRef = useRef(null);

  const accordionData = [
    {
      title: "Complete Profile",
      content: "Build a polished profile once so recruiters can understand your skills, strengths, and preferred roles quickly.",
    },
    {
      title: "Discover Opportunities",
      content: "Get access to roles that are aligned with your experience, interests, and growth goals without digging endlessly.",
    },
    {
      title: "Apply for jobs",
      content: "Move from discovery to action with a simple, guided flow designed to keep applications smooth and focused.",
    },
  ];

  const companies = [
    "Honeywell",
    "Alphabet",
    "Tesla",
    "Microsoft",
    "Amazon",
    "Netflix",
    "Google",
    "Meta",
    "Adobe",
    "Apple",
    "IBM",
    "Intel",
    "Salesforce",
    "Oracle",
  ];

  const benefits = [
    {
      title: "AI-Powered Matching",
      description: "Our intelligent AI analyses skills and experience to connect you with the most relevant opportunities, saving you time and effort.",
    },
    {
      title: "Personalised Experience",
      description: "Our cutting-edge platform gives you exclusive access to a curated talent pool and makes tailored job recommendations.",
    },
    {
      title: "Real-Time Updates",
      description: "Track application status, get instant notifications, and stay informed throughout the talent and job hunt process.",
    },
  ];

  const jobs = Array.from({ length: 8 }, () => ({
    company: "Webenza India Pvt Ltd.",
    position: "Associate Sales Support Representative",
    salary: "8 LPA",
    location: "Bengaluru",
    experience: "2-5 Years",
    image: Webenza,
  }));

  const reviews = Array.from({ length: 6 }, () => ({
    review:
      "TalentGPT is a great platform for job seekers. It is easy to use and has a lot of job opportunities. I would recommend it to anyone looking for a job.",
    name: "John Doe",
    job: "HR Director @InfoTech",
  }));

  const resolveExternalTarget = (targetUrl) => {
    if (!targetUrl) return null;

    try {
      const resolvedUrl = new URL(targetUrl, window.location.origin);
      const isSameOrigin = resolvedUrl.origin === window.location.origin;
      const isLandingTarget =
        isSameOrigin && (resolvedUrl.pathname === "/" || resolvedUrl.pathname === "/landing");

      if (isLandingTarget) return null;

      return resolvedUrl.toString();
    } catch {
      return targetUrl;
    }
  };

  const handleRoleLogin = (role) => {
    // Clear stale Job Seeker session if present
    if (metadata?.role_id === 6) {
      clearAuthSession();
    }

    if (role === "jobseeker") {
      // Redirect to Job Seeker portal from .env
      const jobSeekerUrl = resolveExternalTarget(process.env.REACT_APP_JOBSEEKER_URL);

      if (jobSeekerUrl) {
        window.location.assign(jobSeekerUrl);
        return;
      }

      navigate("/auth/signup");
      return;
    }

    if (token && metadata?.role_id !== 6) {
      // Already authenticated as HR/admin — go to dashboard
      navigate("/dashboard");
      return;
    }

    // Redirect to Recruiter login from .env
    const recruiterUrl = resolveExternalTarget(process.env.REACT_APP_RECRUITER_URL);

    if (recruiterUrl) {
      window.location.assign(recruiterUrl);
      return;
    }

    navigate("/auth/login");
  };

  const scrollByAmount = (ref, amount) => {
    if (!ref.current) return;
    ref.current.scrollBy({ left: amount, behavior: "smooth" });
  };

  return (
    <div className="landing-page-shell">
      <div className="landing-page-background" />

      <header className="landing-page-header">
        <div className="landing-brand">
          <div className="landing-brand-mark">
            <Logo className="landing-brand-logo" />
          </div>
          <div>
            <p className="landing-brand-title">TalentGPT</p>
            <p className="landing-brand-subtitle">Professional hiring and career discovery</p>
          </div>
        </div>

        <div className="landing-header-actions">
          <button className="landing-button landing-button-secondary" onClick={() => handleRoleLogin("recruiter")} type="button">
            Join as Recruiter
          </button>
          <button
            className="landing-link-button"
            onClick={() => handleRoleLogin("jobseeker")}
            type="button"
          >
            Join as Job Seeker
          </button>
        </div>
      </header>

      <main className="landing-main">
        <section className="landing-hero">
          <div className="landing-hero-copy">
            <div className="landing-brand-inline">
              <Logo className="landing-inline-logo" />
            </div>
            <p className="landing-hero-kicker">Your talent and job hunt sidekick</p>
            <h1>
              Say hello
              <br />
              to <span>TalentGPT!</span>
            </h1>
          </div>

          <div className="landing-hero-visual">
            <div className="landing-intro-panel">
              <p className="landing-intro-text">
                It&apos;s your time to shine. With TalentGPT, stand out and let top companies find you.
                Zero stress, zero hassle, just opportunities tailored for you.
              </p>
              <div className="landing-hero-cta-group">
                <button className="landing-inline-cta" onClick={() => handleRoleLogin("recruiter")} type="button">
                  Join as Recruiter <FaArrowRight />
                </button>
                <button className="landing-inline-cta secondary" onClick={() => handleRoleLogin("jobseeker")} type="button">
                  Join as Job Seeker <FaArrowRight />
                </button>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-showcase">
          <div className="landing-wave-pattern" aria-hidden="true">
            <span />
            <span />
            <span />
            <span />
          </div>

          <div className="landing-spark-shape" aria-hidden="true" />

          <article className="landing-profile-card">
            <div className="landing-profile-top">
              <img src={Person} alt="Candidate profile" />
              <div className="landing-profile-status">
                <span className="success">+</span>
                <span className="danger">x</span>
              </div>
            </div>
            <h3>Riya Singh</h3>
            <p>UI/UX Designer</p>
            <div className="landing-profile-stars">
              <span />
              <span />
              <span />
              <span />
              <span className="muted" />
            </div>
          </article>

          <article className="landing-chart-card">
            <div className="bar short" />
            <div className="bar tiny" />
            <div className="bar mid" />
            <div className="bar accent" />
            <div className="bar tall" />
          </article>

          <div className="landing-person-wrap">
            <img src={EeezoHome} alt="Professional using TalentGPT" className="landing-person-image" />
          </div>
        </section>

        <section className="landing-trust-strip landing-section-block">
          <div className="landing-trust-copy">
            <span>5000+</span> companies
            <br />
            already trust us
          </div>
          <div className="landing-company-list">
            {companies.map((company) => (
              <span key={company}>{company}</span>
            ))}
          </div>
        </section>

        <section className="landing-flow-section landing-section-block">
          <div className="landing-flow-copy">
            <h2>
              How Jobs Find You
              <br />
              <span>First On TalentGPT?</span>
            </h2>
            <div className="landing-accordion-wrap">
              <Accordion data={accordionData} />
            </div>
            <div className="landing-toggle-card">
              <span>For Job Seekers</span>
              <div className="landing-toggle-switch">
                <span />
              </div>
              <span>For Recruiters</span>
            </div>
          </div>
          <div className="landing-flow-visual">
            <div className="landing-flow-stack">
              <div className="landing-flow-card back top">
                <div className="landing-flow-banner" aria-hidden="true" />
                <div className="landing-flow-card-row">
                  <div className="landing-flow-card-logo">e</div>
                  <div className="landing-flow-card-copy">
                    <span className="landing-flow-line long" />
                    <span className="landing-flow-line short" />
                  </div>
                  <div className="landing-flow-bookmark" />
                </div>
                <div className="landing-flow-card-tags">
                  <span />
                  <span />
                  <span />
                  <span />
                </div>
              </div>

              <div className="landing-flow-card front">
                <div className="landing-flow-card-header">
                  <div className="landing-flow-card-row">
                    <div className="landing-flow-card-logo">e</div>
                    <div className="landing-flow-card-title">
                      <small>Webenza India Pvt Ltd.</small>
                      <strong>Associate Sales Support Representative</strong>
                    </div>
                    <div className="landing-flow-bookmark active" />
                  </div>
                </div>

                <div className="landing-flow-pill-row">
                  <span>2-5 Years</span>
                  <span>On-Site</span>
                  <span>Bengaluru</span>
                  <span>8 LPA</span>
                </div>

                <div className="landing-flow-divider" />

                <div className="landing-flow-footer">
                  <div>
                    <small>job match rate: high</small>
                    <p>You were born for something like this!</p>
                  </div>
                  <button type="button">View Job</button>
                </div>
              </div>

              <div className="landing-flow-card back bottom">
                <div className="landing-flow-card-row">
                  <div className="landing-flow-card-logo">e</div>
                  <div className="landing-flow-card-copy">
                    <strong>Lead Sales Support Representative</strong>
                    <div className="landing-flow-card-tags faint">
                      <span />
                      <span />
                      <span />
                      <span />
                    </div>
                  </div>
                  <div className="landing-flow-bookmark" />
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="landing-marquee">
          <div className="landing-marquee-track">
            {Array.from({ length: 3 }).map((_, trackIndex) => (
              <React.Fragment key={trackIndex}>
                <span>Job Satisfaction</span>
                <span className="accent">X</span>
                <span>AI Precision</span>
                <span className="accent">X</span>
              </React.Fragment>
            ))}
          </div>
        </section>

        <section className="landing-benefits landing-section-block">
          <div className="landing-section-heading">
            <h2>
              Why <span>Choose Us</span>
            </h2>
            <p>
              Real connections, real opportunities. Job seekers find their dream roles, recruiters find their ideal
              candidates and we make it all happen.
            </p>
          </div>
          <div className="landing-benefits-panel">
            {benefits.map((benefit) => (
              <article className="landing-benefit-card" key={benefit.title}>
                <img src={Person} alt="" />
                <h3>{benefit.title}</h3>
                <p>{benefit.description}</p>
              </article>
            ))}
            <img className="landing-benefits-art" src={eeezoJob} alt="" />
          </div>
        </section>

        <section className="landing-jobs landing-section-block">
          <div className="landing-jobs-head">
            <div>
              <h2>
                Featured <span>Jobs</span>
              </h2>
              <p>Ready for a change? Browse our hand-picked opportunities for you and apply now.</p>
            </div>
            <div className="landing-nav-buttons">
              <button type="button" onClick={() => scrollByAmount(jobsRef, -320)}>
                <FaArrowLeft />
              </button>
              <button type="button" onClick={() => scrollByAmount(jobsRef, 320)}>
                <FaArrowRight />
              </button>
            </div>
          </div>
          <div className="landing-jobs-row" ref={jobsRef}>
            {jobs.map((job, index) => (
              <article className="landing-job-card" key={`${job.company}-${index}`}>
                <img src={job.image} alt={job.company} />
                <p className="landing-job-company">{job.company}</p>
                <h3>{job.position}</h3>
                <div className="landing-job-meta">
                  <span>{job.experience}</span>
                  <span>{job.location}</span>
                  <span>{job.salary}</span>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-testimonials landing-section-block">
          <div className="landing-testimonials-head">
            <div>
              <h2>
                Real Results, <span>Real People</span>
              </h2>
            </div>
            <p>
              Don&apos;t just take our word for it. See what our users are saying about their TalentGPT experience.
            </p>
          </div>
          <div className="landing-testimonials-panel">
            <img className="landing-testimonial-art" src={eeezoTestimonial} alt="" />
            <div className="landing-testimonial-row" ref={testimonialsRef}>
              {reviews.map((review, index) => (
                <article className="landing-testimonial-card" key={`${review.name}-${index}`}>
                  <div className="landing-stars">
                    {Array.from({ length: 4 }).map((_, starIndex) => (
                      <FaStar key={starIndex} />
                    ))}
                    <FaStar className="muted" />
                  </div>
                  <p>{review.review}</p>
                  <div className="landing-review-author">
                    <img src={Person} alt={review.name} />
                    <div>
                      <strong>{review.name}</strong>
                      <span>{review.job}</span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
            <div className="landing-nav-buttons center">
              <button type="button" onClick={() => scrollByAmount(testimonialsRef, -340)}>
                <FaArrowLeft />
              </button>
              <button type="button" onClick={() => scrollByAmount(testimonialsRef, 340)}>
                <FaArrowRight />
              </button>
            </div>
          </div>
        </section>

        <footer className="landing-footer">
          <div className="landing-footer-top">
            <div>
              <p className="landing-footer-copy">
                TalentGPT helps recruiters and candidates connect through a cleaner, smarter, and more human hiring
                experience.
              </p>
              <div className="landing-socials">
                {[fblogo, instalogo, linkedinlogo, twitterlogo, youtubelogo].map((icon, index) => (
                  <a href="/" key={index} onClick={(event) => event.preventDefault()}>
                    <img src={icon} alt="" />
                  </a>
                ))}
              </div>
            </div>

            <div className="landing-contact-block">
              <h3>Get in Touch with Us</h3>
              <p>
                <FaMapMarkerAlt /> Bengaluru, India
              </p>
              <p>
                <FaPhoneAlt /> +123 345123 556
              </p>
              <p>
                <FaEnvelope /> support@talentgpt.ai
              </p>
            </div>
          </div>
          <div className="landing-footer-bottom">© 2024, TalentGPT. All rights reserved.</div>
        </footer>
      </main>
    </div>
  );
};

export default LandingPage;
